/**
 * One fetch-timeout for every Supabase call, instead of a wrapper per call.
 *
 * PR #220 (the 5 Aug staging outage) put 12s `withTimeout`s around the six
 * calls the route guard depends on. Every OTHER call still had none: a
 * hanging connection held its React Query in `pending` forever — no data,
 * no error, no BackendUnreachable, just a spinner. This wrapper bounds every
 * request the supabase-js client makes, at the network layer.
 *
 * The AuthContext `withTimeout`s stay: they bound the whole promise, which
 * covers two hangs this layer cannot see — auth-js's internal lock
 * acquisition (not a fetch), and a server that sends headers but then stalls
 * the BODY: our timer is cleared the moment fetch() resolves, which is at
 * headers-received. Two layers, different hangs.
 *
 * Why an explicit AbortController instead of AbortSignal.timeout(): the
 * timer must be cleared when the request settles, and the caller's own
 * signal (React Query cancellation) must keep working alongside ours.
 * (AbortSignal.any would merge the signals in one line, but it needs
 * Chrome 116 / Safari 17.4 and this app builds for chrome87/safari14 —
 * esbuild transpiles syntax, never runtime APIs.)
 */
import { TimeoutError } from "./withTimeout";

/** What kind of Supabase endpoint a URL addresses. */
export type SupabaseEndpoint = "auth" | "rest" | "functions" | "storage" | "other";

export function classifyEndpoint(url: string): SupabaseEndpoint {
  if (url.includes("/auth/v1/")) return "auth";
  if (url.includes("/rest/v1/")) return "rest";
  if (url.includes("/functions/v1/")) return "functions";
  if (url.includes("/storage/v1/")) return "storage";
  return "other";
}

/**
 * How long each endpoint class may take, in ms. `null` = no bound.
 *
 * - auth: token refreshes are tiny; 15s matches the spirit of PR #220's 12s.
 * - rest: generous on purpose. The critical reads already fail at 12s via
 *   withTimeout; this is the safety net for everything else. 60s, not 30:
 *   a big schedule save (hundreds of KB of JSONB) on a ~20 KB/s festival
 *   uplink is slow but ALIVE, and aborting it would fail a save that was
 *   about to land. The bound exists to end dead connections, not slow ones.
 * - functions: AI generation (schedule assistant, briefing weather) runs the
 *   better part of a minute by design; 180s sits above every legitimate run
 *   while still ending a truly dead connection.
 * - storage: a 5MB festival map upload over bad LTE is slow but alive; 120s.
 */
const TIMEOUT_MS: Record<SupabaseEndpoint, number | null> = {
  auth: 15_000,
  rest: 60_000,
  functions: 180_000,
  storage: 120_000,
  other: null,
};

export function timeoutMsFor(url: string): number | null {
  return TIMEOUT_MS[classifyEndpoint(url)];
}

/** The label inside the TimeoutError; names the endpoint class, not the URL. */
export function timeoutMessage(url: string): string {
  return `supabase ${classifyEndpoint(url)} request`;
}

/**
 * A fetch with the policy above applied. A timed-out request rejects with a
 * TimeoutError, which auth-js maps to a RETRYABLE error (session kept) and
 * postgrest-js to a returned error — both the outage behaviours we want.
 */
export function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  const ms = timeoutMsFor(url);
  if (ms === null) return fetch(input, init);

  const controller = new AbortController();
  // The abort REASON is what fetch rejects with. TimeoutError — the same
  // class the promise layer throws — so one outage is one Sentry family,
  // and a future "ignore benign AbortError cancellations" filter can never
  // swallow a real timeout.
  const timer = setTimeout(() => controller.abort(new TimeoutError(timeoutMessage(url), ms)), ms);

  // The caller's own signal (React Query cancellation, auth-js internals)
  // must still be able to abort us. A Request object carries a signal of
  // its own, which spreading `init` would silently override.
  const callerSignal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else
      callerSignal.addEventListener("abort", () => controller.abort(callerSignal.reason), {
        once: true,
      });
  }

  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}
